import { useCallback } from 'react'
import { ReturnFormData } from '../../components/EditReturnItemModal'
import { useReturnAllModal } from '../../components/ReturnAllModal'

interface OrderItem {
  id?: number
  product?: {
    id?: number
  }
  clearanceItems?: any[]
  quantityAvailableToReturn: number
}

interface UseReturnAllProps {
  orderItemsAvailableToReturn: OrderItem[]
  defaultDepotId?: number
  checkoutDepotId?: number
  onAddReturn: (returnData: ReturnFormData) => Promise<void>
}

export const useReturnAll = ({
  orderItemsAvailableToReturn,
  defaultDepotId,
  checkoutDepotId,
  onAddReturn,
}: UseReturnAllProps) => {
  const { returnAllModalProps, open: openReturnAllModal } = useReturnAllModal()

  // Handle returning all items with original positions
  const handleReturnAllItems = useCallback(
    async (overwritePositions?: string[], overwriteDepotId?: number) => {
      const promises = orderItemsAvailableToReturn.map(item => {
        if (!item.id) return Promise.resolve() // Skip items without id
        const returnData: ReturnFormData = {
          orderItemId: item.id,
          productId: item.product?.id || 0,
          amount: item.quantityAvailableToReturn,
          depotId:
            overwriteDepotId ||
            item?.clearanceItems?.[0]?.productDepots?.[0]?.depot?.id ||
            checkoutDepotId ||
            defaultDepotId ||
            0,
          batch: item.clearanceItems?.[0]?.batch || null,
          expirationDate: item.clearanceItems?.[0]?.expirationDate || null,
          ...(overwritePositions
            ? {
                position1: overwritePositions[0] || '',
                position2: overwritePositions[1] || '',
                position3: overwritePositions[2] || '',
              }
            : {
                position1: item.clearanceItems?.[0]?.position1 || '',
                position2: item.clearanceItems?.[0]?.position2 || '',
                position3: item.clearanceItems?.[0]?.position3 || '',
              }),
        }
        return onAddReturn(returnData)
      })

      await Promise.all(promises)
    },
    [orderItemsAvailableToReturn, checkoutDepotId, defaultDepotId, onAddReturn],
  )

  // Handle returning all items with modal for new position
  const handleReturnAllItemsWithModal = useCallback(async () => {
    openReturnAllModal(async ({ position1, position2, position3, depotId }) => {
      await handleReturnAllItems([position1, position2, position3], depotId)
    })
  }, [openReturnAllModal, handleReturnAllItems])

  return {
    returnAllModalProps,
    handleReturnAllItems,
    handleReturnAllItemsWithModal,
  }
}
